<template>
    <MainLayout />
</template>

<script setup>
import { onMounted } from 'vue'
import MainLayout from '@/layouts/MainLayout.vue'
import { useThemeMode } from '@/composables/useThemeMode'

const { applyTheme } = useThemeMode()

// 애플 로그인 redirect 결과 처리
onMounted(async () => {
    // 저장된 테마 즉시 적용
    applyTheme()
    try {
        const [{ getAuth, getRedirectResult }, { useAuthStore }] = await Promise.all([
            import('firebase/auth'),
            import('@/stores/auth'),
        ])
        const auth = getAuth()
        const authStore = useAuthStore()
        
        console.log('App.vue 마운트: redirect 결과 확인 중...')
        console.log('현재 URL:', window.location.href)
        console.log('URL 파라미터:', window.location.search)
        console.log('URL 해시:', window.location.hash)
        
        // 리디렉션 결과를 먼저 확인 (인증 상태 초기화 전에)
        // getRedirectResult는 한 번만 호출할 수 있으므로 먼저 호출해야 함
        const result = await getRedirectResult(auth)
        
        if (result) {
            const user = result.user
            console.log('✅ 애플 로그인 성공 (redirect):', user.email)
            console.log('사용자 정보:', {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL,
                providerId: user.providerId,
                providerData: user.providerData
            })
            
            try {
                await authStore.ensureUserDocument(user)
            } catch (saveError) {
                console.error('사용자 문서 생성 실패:', saveError)
            }
            
            // 인증 상태 초기화 (사용자 정보 반영)
            await authStore.initAuth()
            
            // URL 정리 (리디렉션 파라미터 제거)
            if (window.location.search || window.location.hash) {
                window.history.replaceState({}, document.title, window.location.pathname)
            }
            
            // 성공 메시지
            alert(`환영합니다, ${user.displayName || user.email || '사용자'}님!`)
            
            // 앱 홈으로 이동 (필요시)
            const router = await import('@/router')
            if (router.default.currentRoute.value.path !== '/app') {
                router.default.push('/app')
            }
        } else {
            console.log('ℹ️ redirect 결과 없음')
            
            // URL에 에러 파라미터가 있는지 확인
            const urlParams = new URLSearchParams(window.location.search)
            const errorCode = urlParams.get('error')
            const errorDescription = urlParams.get('error_description')
            
            if (errorCode) {
                console.error('❌ 리디렉션 에러 발견:', errorCode, errorDescription)
                alert(`로그인 실패: ${errorDescription || errorCode}`)
                // URL 정리
                window.history.replaceState({}, document.title, window.location.pathname)
            } else {
                // 에러가 없으면 정상적인 접속이거나 리디렉션이 완료된 후일 수 있음
                console.log('정상적인 접속 또는 이미 로그인된 상태일 수 있음')
                
                // sessionStorage에 Apple 로그인 시도 플래그 확인
                const wasAppleLoginAttempt = sessionStorage.getItem('appleLoginAttempt')
                console.log('Apple 로그인 시도 플래그:', wasAppleLoginAttempt)
                
                // 인증 상태 초기화 (기존 사용자 확인)
                await authStore.initAuth()
                
                console.log('인증 상태 확인:', {
                    isAuthenticated: authStore.isAuthenticated,
                    user: authStore.user ? {
                        uid: authStore.user.uid,
                        email: authStore.user.email,
                        providerId: authStore.user.providerId
                    } : null
                })
                
                // 만약 사용자가 로그인되어 있고, 이전에 Apple 로그인을 시도했다면
                if (wasAppleLoginAttempt && authStore.isAuthenticated) {
                    console.log('✅ Apple 로그인 완료 (onAuthStateChanged를 통해 확인됨)')
                    sessionStorage.removeItem('appleLoginAttempt')
                    
                    if (authStore.user) {
                        try {
                            await authStore.ensureUserDocument(authStore.user)
                        } catch (saveError) {
                            console.error('사용자 문서 생성 실패:', saveError)
                        }
                    }
                    
                    alert(`환영합니다, ${authStore.userEmail || '사용자'}님!`)
                } else if (wasAppleLoginAttempt && !authStore.isAuthenticated) {
                    // 플래그는 있지만 로그인되지 않음 - 커스텀 도메인 + redirect 유실 가능
                    console.warn('⚠️ Apple 로그인 시도했지만 로그인되지 않음')
                    sessionStorage.removeItem('appleLoginAttempt')
                    sessionStorage.removeItem('appleLoginTimestamp')
                    alert('Apple 로그인이 완료되지 않았어요. 팝업이 허용된 브라우저에서 다시 시도해 주세요.')
                }
            }
        }
    } catch (error) {
        console.error('❌ 애플 로그인 redirect 결과 처리 실패:', error)
        console.error('에러 코드:', error.code)
        console.error('에러 메시지:', error.message)
        console.error('전체 에러:', error)
        
        // 사용자에게 에러 표시
        const errorMessage = error.message || '로그인 처리 중 오류가 발생했습니다.'
        alert(`로그인 실패: ${errorMessage}`)
        
        // URL 정리
        if (window.location.search || window.location.hash) {
            window.history.replaceState({}, document.title, window.location.pathname)
        }
        
        // 인증 상태 초기화 (에러가 발생해도 시도)
        try {
            const { useAuthStore } = await import('@/stores/auth')
            const authStore = useAuthStore()
            await authStore.initAuth()
        } catch (initError) {
            console.error('인증 상태 초기화 실패:', initError)
        }
    }
})

</script>

<style lang="scss">
// @import '@/assets/main.scss';
</style>